/*  iceberg-ui  */
import { Box, Button, Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Alert } from '@mui/material';
import { CellContext } from '@tanstack/react-table';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { AddUser } from 'application/Shared/Components/Buttons/AddUser';
import {
    BrowserBubblesCell,
    DateFormatCell,
    SelectRowCell,
    UserCell,
    browserFilter,
    dateFilter,
    filterBrowsers,
    lastXDays
} from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { sortByTimestamp } from 'application/Shared/Components/TableSortFunctions';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { TABLENAMES } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import { User } from 'application/Shared/types/user';

/* NPM */
import { subDays } from 'date-fns';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import { AdminUsersCharts } from './internal/AdminUsersCharts';
import { AdminUsersTable } from './internal/AdminUsersTable';
import {
    AdminUsersTableContextProps,
    AdminUsersTableContextProvider,
    useAdminUsersTableContext
} from './internal/AdminUsersTableContext';

const AddUserButton = () => {
    const { setRerender } = useManagedTableV2Context<User>();
    const { clientId } = useParams();

    return (
        <AddUser
            clientId={clientId}
            data-trackid="addNewUser_client"
            rerender={() => setRerender!(true)}
        />
    );
};

const NewClientNotice = () => {
    const { rowData: data } = useManagedTableV2Context<User>();
    const [showNotice, setShowNotice] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        let firstUserDate = new Date(); // today

        // Data is null on initial load.
        if (!data) {
            return;
        }

        data?.forEach((user) => {
            const dateCreated = new Date(user.dateCreated);

            if (dateCreated < firstUserDate) {
                firstUserDate = dateCreated;
            }
        });

        // If two days ago
        setShowNotice(firstUserDate > subDays(new Date(), 2));
    }, [data]);

    if (showNotice) {
        return (
            <Alert severity="info" icon={false} sx={{ marginBottom: '24px' }}>
                {t('Users.newClientNotice')}
            </Alert>
        );
    }
    return null;
};

const ActionsCell = ({ row }: CellContext<User, undefined>) => {
    const { t } = useTranslation();
    const {
        baseRoute,
        navigate,
        isSessionANM,
        setDisabling,
        setEnabling,
        setOpenSendEmail
    } = useAdminUsersTableContext();
    return (
        <Flex alignItems="center" justifyContent="flex-end">
            <KebabMenu
                options={[
                    {
                        key: 'view',
                        label: t('Common.view'),
                        value: 'view'
                    },
                    {
                        hide: !(
                            Helpers.isEmail(row.original.email) && !isSessionANM
                        ),
                        key: 'sendEmail',
                        label: t('Users.invite'),
                        value: 'sendEmail'
                    },
                    {
                        key: 'disableEnableActivityCollection',
                        label: row.original.disabled
                            ? t('Common.enableUser')
                            : t('Common.disableUser'),
                        value: row.original.disabled ? 'enable' : 'disable'
                    }
                ]}
                onClick={(value: string) => {
                    if (value === 'disable') {
                        setDisabling(row.original.id);
                    }
                    if (value === 'enable') {
                        setEnabling(row.original.id);
                    }
                    if (value === 'sendEmail') {
                        setOpenSendEmail(row.original.email);
                    }
                    if (value === 'view') {
                        navigate(
                            `${baseRoute}/user/${row.original.id}/overview`
                        );
                    }
                }}
                data-testid="usersTableActions"
            />
        </Flex>
    );
};

const UserMergeAction = ({
    setOpenMergeModal
}: Partial<AdminUsersTableContextProps>) => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context<User>();

    const getTitle = () => {
        if (table.getSelectedRowModel().rows.length > 2) {
            return t('Users.mergeUsers.noMoreThanTwo');
        }
        if (table.getSelectedRowModel().rows.length === 1) {
            return t('Users.mergeUsers.moreThanOne');
        }
        return null;
    };

    return (
        <DarkTooltip arrow title={getTitle()}>
            {/* Need a box to show the MUI tooltip since the button is disabled */}
            <Box>
                <Button
                    data-testid="adminUsersMergeBtn"
                    data-trackid="admin_users_userMerge_openModal"
                    disabled={table.getSelectedRowModel().rows.length !== 2}
                    onClick={() => setOpenMergeModal!(true)}
                    size="small"
                    variant="inverted"
                >
                    {t('Users.mergeUsers')}
                </Button>
            </Box>
        </DarkTooltip>
    );
};

const columns = [
    {
        cell: SelectRowCell,
        enableHiding: false,
        enableSorting: false,
        id: 'select',
        maxSize: 50,
        meta: { align: 'center' },
        size: 50
    },
    {
        accessorKey: 'lastName',
        cell: UserCell,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'name',
        name: 'Common.name',
        size: 200,
        testId: 'name'
    },
    {
        accessorKey: 'lastCheckIn',
        cell: DateFormatCell,
        filterFn: lastXDays,
        header: Header,
        id: 'lastCheckIn',
        meta: { filter: dateFilter },
        name: 'Common.last_check_in',
        size: 100,
        sortingFn: sortByTimestamp,
        testId: 'lastCheckIn'
    },
    {
        accessorKey: 'dateLastManaged',
        cell: DateFormatCell,
        filterFn: lastXDays,
        header: Header,
        id: 'dateLastManaged',
        meta: { filter: dateFilter },
        name: 'Users.last_activity',
        size: 100,
        sortingFn: sortByTimestamp,
        testId: 'dateLastManaged'
    },
    {
        accessorKey: 'createdBy',
        cell: ({ cell }: CellContext<User, unknown>) => cell.getValue() || '-',
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'createdBy',
        name: 'Common.created_by',
        size: 100,
        testId: 'createdBy'
    },
    {
        accessorKey: 'browsers',
        cell: BrowserBubblesCell,
        filterFn: filterBrowsers,
        header: Header,
        id: 'browsers',
        meta: { filter: browserFilter },
        name: 'Common.browsers',
        size: 100,
        testId: 'browsers'
    },
    {
        accessorKey: 'email',
        enableHiding: false,
        header: Header,
        id: 'email',
        name: 'Common.email',
        size: 200
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        meta: {
            align: 'right',
            header: { justifyContent: 'flex-end' }
        },
        name: 'Common.actions',
        size: 50,
        testId: 'actions'
    }
];

const baseParameters = {
    ignoreDisabled: true,
    includeBrowsers: true,
    includeCreatedBy: true,
    includeDomainManaged: true,
    size: -1,
    smartSort: true
};

const AdminUsersPage = () => {
    const { t } = useTranslation();
    const { setOpenMergeModal } = useAdminUsersTableContext();
    const [resetRowSelection, setResetRowSelection] = useState(false);

    const toggles = useMemo(
        () => [
            {
                defaultEnabled: true,
                key: 'enabled',
                parameter: { field: 'enabled', value: true },
                toggleText: t('Users.exclude_disabled'),
                tooltip: 'Users.exclude_disabled'
            },
            {
                defaultEnabled: true,
                key: 'excludeExpired',
                parameter: { field: 'excludeExpired', value: true },
                toggleText: t('Users.exclude_expired'),
                tooltip: 'Users.exclude_expired'
            },
            {
                defaultEnabled: false,
                key: 'hasActivity',
                parameter: { field: 'hasActivity', value: true },
                toggleText: t('Users.exclude_no_activity')
            }
        ],
        [t]
    );

    const columnPinning = {
        left: ['select', 'name'],
        right: ['actions']
    };

    return (
        <ManagedTableV2ContextProvider
            baseParameters={baseParameters}
            bulkActions={
                <>
                    <AddUserButton />
                    <UserMergeAction setOpenMergeModal={setOpenMergeModal} />
                </>
            }
            columns={columns}
            getRowId="id"
            ignoreSearchParams
            initialState={{ columnPinning }}
            mode="client"
            resetRowSelection={resetRowSelection}
            rowsApi="/user"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            setResetRowSelection={setResetRowSelection}
            sortDirection="ASC"
            sortKey="name"
            toggles={toggles}
            tableName={TABLENAMES.ADMIN_USERS_PAGE}
        >
            <PageTitle title={t('Common.users')} />
            <NewClientNotice />
            <AdminUsersCharts />
            <Card marginTop="24px">
                <AdminUsersTable setResetRowSelection={setResetRowSelection} />
            </Card>
        </ManagedTableV2ContextProvider>
    );
};

export const AdminUsers = () => (
    <AdminUsersTableContextProvider>
        <AdminUsersPage />
    </AdminUsersTableContextProvider>
);
